/**
 * PO Items Fields Component
 *
 * Renders the line items section of the Purchase Order form.
 * Each item includes: Line ID, SKU, Vendor Part, Quantity, Price, UOM, Description.
 *
 * Uses react-hook-form's useFieldArray for dynamic array management.
 *
 * This maps to:
 * - X12 850: PO1 Loop (Baseline Item Data)
 * - EDIFACT ORDERS: LIN segments (Line Item)
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { formatCurrency } from '../../utils/formatCurrency';
import { isFieldEmpty, normalizeConfidence, getFieldScore, getFieldIssues, unwrapEvaluation } from '../../utils/confidenceUtils';

/**
 * Severity-to-color map for inline issue indicators (matches Input.jsx)
 */
const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Inline issues indicator (warning triangle + hover tooltip).
 * Used for totals labels where the Input component has no `label` prop.
 */
function IssuesIndicator({ issues }) {
  if (!issues || issues.length === 0) return null;

  const worstSeverity = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';

  return (
    <span className="relative group/issues inline-flex ml-1">
      <svg
        className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worstSeverity]?.icon || 'text-amber-500'} cursor-help`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div
              key={idx}
              className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}
            >
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>
                {formatIssueType(issue.type)}
              </p>
              {issue.detail && (
                <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

/**
 * POItemsFields Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.form - React Hook Form instance
 * @param {Object} props.confidence - Confidence scores from AI extraction
 *
 * @example
 * <POItemsFields
 *   form={form}
 *   confidence={{ items: 0.75 }}
 * />
 */
function POItemsFields({ form, evaluation: rawEvaluation, confidence = {}, issues = [] }) {
  const evaluation = unwrapEvaluation(rawEvaluation);
  // Destructure form methods
  const { register, control, formState: { errors }, watch } = form;

  // useFieldArray manages the dynamic items array
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  // Watch items, currency, and totals
  const items = watch('items') || [];
  const currency = watch('header.currency') || 'USD';
  const totals = watch('totals') || {};

  /**
   * Add a new empty line item
   */
  const addItem = () => {
    append({
      lineId: String(fields.length + 1),
      sku: '',
      vendorPart: '',
      quantity: 1,
      unitPrice: 0,
      uom: 'EA',
      description: ''
    });
  };

  /**
   * Calculate line total
   * @param {Object} item - Line item
   * @returns {number} Line total
   */
  const calculateLineTotal = (item) => {
    const qty = parseFloat(item?.quantity) || 0;
    const price = parseFloat(item?.unitPrice) || 0;
    return qty * price;
  };

  /**
   * Calculate grand total
   * @returns {number} Sum of all line totals
   */
  const calculateGrandTotal = () => {
    return items.reduce((sum, item) => sum + calculateLineTotal(item), 0);
  };

  /**
   * Get per-field score for a specific item.
   * Checks LLM-as-judge evaluation first (paths like "items[0].sku"),
   * then falls back to legacy confidence format.
   * @param {number} index - Item index
   * @param {string} fieldName - Field name (e.g. 'sku', 'unitPrice')
   * @returns {number|undefined} Score or undefined
   */
  const getItemConfidence = (index, fieldName) => {
    // Try new evaluation format first — field paths like "items[0].sku"
    const evalScore = getFieldScore(evaluation, null, `items[${index}].${fieldName}`);
    if (evalScore != null) return evalScore;

    // Fall back to legacy confidence format
    const itemsConf = confidence.items;
    let conf;
    if (Array.isArray(itemsConf) && itemsConf[index]) {
      conf = itemsConf[index][fieldName];
    } else if (typeof itemsConf === 'number') {
      conf = itemsConf;
    }
    if (conf === undefined) return undefined;
    const val = items[index]?.[fieldName];
    return normalizeConfidence(conf, isFieldEmpty(val));
  };

  /**
   * Check if a specific item has any low-score field.
   * Checks evaluation field_scores first, then legacy confidence.
   */
  const isItemLowConfidence = (index) => {
    // Check evaluation field_scores for any low-scoring item field
    if (evaluation?.field_scores) {
      const prefix = `items[${index}].`;
      return Object.entries(evaluation.field_scores).some(([path, fieldData]) => {
        if (!path.startsWith(prefix)) return false;
        const score = typeof fieldData === 'object' ? fieldData.score : fieldData;
        return score != null && score < 0.8;
      });
    }

    // Fall back to legacy confidence
    const itemsConf = confidence.items;
    if (Array.isArray(itemsConf) && itemsConf[index]) {
      const itemData = items[index];
      return Object.entries(itemsConf[index]).some(([key, conf]) => {
        if (typeof conf !== 'number') return false;
        const val = itemData?.[key];
        return normalizeConfidence(conf, isFieldEmpty(val)) < 0.8;
      });
    }
    if (typeof itemsConf === 'number') {
      return itemsConf < 0.8;
    }
    return false;
  };

  /**
   * Compute aggregate score for the section header.
   * Checks evaluation field_scores first, then legacy confidence.
   * Uses min across all item fields.
   */
  const getAggregateConfidence = () => {
    // Check evaluation field_scores for item fields
    if (evaluation?.field_scores) {
      let min = 1;
      let hasRelevant = false;
      for (const [path, fieldData] of Object.entries(evaluation.field_scores)) {
        if (!path.startsWith('items[')) continue;
        const score = typeof fieldData === 'object' ? fieldData.score : fieldData;
        if (score == null) continue;
        hasRelevant = true;
        if (score < min) min = score;
      }
      return hasRelevant ? min : undefined;
    }

    // Fall back to legacy confidence
    const itemsConf = confidence.items;
    if (Array.isArray(itemsConf) && itemsConf.length > 0) {
      let min = 1;
      let hasRelevant = false;
      itemsConf.forEach((itemObj, itemIndex) => {
        if (!itemObj || typeof itemObj !== 'object') return;
        const itemData = items[itemIndex];
        for (const [key, conf] of Object.entries(itemObj)) {
          if (typeof conf !== 'number') continue;
          const val = itemData?.[key];
          const normalized = normalizeConfidence(conf, isFieldEmpty(val));
          hasRelevant = true;
          if (normalized < min) min = normalized;
        }
      });
      return hasRelevant ? min : undefined;
    }
    if (typeof itemsConf === 'number') return itemsConf;
    return undefined;
  };

  /**
   * Get per-field score for a totals field.
   * Checks LLM-as-judge evaluation first (paths like "totals.tax"),
   * then falls back to legacy confidence format.
   * @param {string} fieldName - Field name (e.g. 'tax', 'grandTotal')
   * @returns {number|undefined} Score or undefined
   */
  const getTotalsConfidence = (fieldName) => {
    return getFieldScore(evaluation, confidence, `totals.${fieldName}`);
  };

  const aggregateConfidence = getAggregateConfidence();
  const hasItemsConfidence = aggregateConfidence !== undefined;
  const hasLowConfidence = hasItemsConfidence && aggregateConfidence < 0.8;

  return (
    <div className="space-y-4">
      {/* Section Title with confidence indicator */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Line Items
          {hasItemsConfidence && (
            <span className={`ml-2 text-sm font-normal ${hasLowConfidence ? 'text-yellow-600' : 'text-green-600'}`}>
              ({Math.round(aggregateConfidence * 100)}%)
            </span>
          )}
        </h3>
        <Button variant="secondary" size="sm" onClick={addItem}>
          + Add Item
        </Button>
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No line items added yet. Click "Add Item" to add products or services.
        </div>
      )}

      {/* Line items */}
      {fields.map((field, index) => (
        <div
          key={field.id}
          className={`
            border rounded-lg p-4 space-y-3
            ${isItemLowConfidence(index) ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}
          `}
        >
          {/* Item header with line number and remove button */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Line #{index + 1}
            </span>
            <div className="flex items-center space-x-4">
              {/* Line total */}
              <span className="text-sm text-gray-600">
                Line Total: {formatCurrency(calculateLineTotal(items[index]), currency)}
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            </div>
          </div>

          {/* Item fields grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Line ID */}
            <Input
              label="Line ID"
              {...register(`items.${index}.lineId`)}
              error={errors.items?.[index]?.lineId}
              confidence={getItemConfidence(index, 'lineId')}
              issues={getFieldIssues(issues, `items[${index}].lineId`)}
              placeholder="1"
            />

            {/* SKU (Buyer's part number) */}
            <Input
              label="SKU"
              {...register(`items.${index}.sku`)}
              error={errors.items?.[index]?.sku}
              confidence={getItemConfidence(index, 'sku')}
              issues={getFieldIssues(issues, `items[${index}].sku`)}
              placeholder="Buyer part #"
            />

            {/* Vendor Part Number */}
            <Input
              label="Vendor Part"
              {...register(`items.${index}.vendorPart`)}
              error={errors.items?.[index]?.vendorPart}
              confidence={getItemConfidence(index, 'vendorPart')}
              issues={getFieldIssues(issues, `items[${index}].vendorPart`)}
              placeholder="Vendor part #"
            />

            {/* Unit of Measure */}
            <Input
              label="UOM"
              {...register(`items.${index}.uom`)}
              error={errors.items?.[index]?.uom}
              confidence={getItemConfidence(index, 'uom')}
              issues={getFieldIssues(issues, `items[${index}].uom`)}
              placeholder="EA, CS, BOX"
            />

            {/* Quantity */}
            <Input
              label="Quantity"
              type="number"
              step="1"
              min="0"
              {...register(`items.${index}.quantity`, {
                required: 'Quantity is required',
                min: { value: 0, message: 'Quantity must be positive' },
                valueAsNumber: true
              })}
              error={errors.items?.[index]?.quantity}
              confidence={getItemConfidence(index, 'quantity')}
              issues={getFieldIssues(issues, `items[${index}].quantity`)}
            />

            {/* Unit Price */}
            <Input
              label="Unit Price"
              type="number"
              step="0.01"
              min="0"
              {...register(`items.${index}.unitPrice`, {
                required: 'Price is required',
                min: { value: 0, message: 'Price must be positive' },
                valueAsNumber: true
              })}
              error={errors.items?.[index]?.unitPrice}
              confidence={getItemConfidence(index, 'unitPrice')}
              issues={getFieldIssues(issues, `items[${index}].unitPrice`)}
            />

            {/* Description - spans 2 columns */}
            <div className="col-span-2">
              <Input
                label="Description"
                {...register(`items.${index}.description`)}
                error={errors.items?.[index]?.description}
                confidence={getItemConfidence(index, 'description')}
                issues={getFieldIssues(issues, `items[${index}].description`)}
                placeholder="Item description"
              />
            </div>
          </div>
        </div>
      ))}

      {/* Totals Summary */}
      {fields.length > 0 && (
        <div className="flex justify-end pt-4 border-t">
          <div className="w-full max-w-sm space-y-2">
            {/* Computed line items subtotal */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Subtotal (line items):</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(calculateGrandTotal(), currency)}
              </span>
            </div>

            {/* Tax */}
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm text-gray-600 whitespace-nowrap inline-flex items-center">
                Tax:
                {getTotalsConfidence('tax') != null && (
                  <span className={`ml-1 text-xs ${getTotalsConfidence('tax') < 0.8 ? 'text-yellow-600' : 'text-green-600'}`}>
                    ({Math.round(getTotalsConfidence('tax') * 100)}%)
                  </span>
                )}
                <IssuesIndicator issues={getFieldIssues(issues, 'totals.tax')} />
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                className="w-36 text-right"
                confidence={getTotalsConfidence('tax')}
                issues={getFieldIssues(issues, 'totals.tax')}
                {...register('totals.tax', { valueAsNumber: true })}
              />
            </div>

            {/* Shipping - show if has a value OR if evaluator flagged it OR has issues */}
            {(totals.shipping != null && totals.shipping !== 0
              || (getTotalsConfidence('shipping') != null && getTotalsConfidence('shipping') < 0.8)
              || getFieldIssues(issues, 'totals.shipping').length > 0) && (
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm text-gray-600 whitespace-nowrap inline-flex items-center">
                  Shipping:
                  {getTotalsConfidence('shipping') != null && (
                    <span className={`ml-1 text-xs ${getTotalsConfidence('shipping') < 0.8 ? 'text-yellow-600' : 'text-green-600'}`}>
                      ({Math.round(getTotalsConfidence('shipping') * 100)}%)
                    </span>
                  )}
                  <IssuesIndicator issues={getFieldIssues(issues, 'totals.shipping')} />
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="w-36 text-right"
                  confidence={getTotalsConfidence('shipping')}
                  issues={getFieldIssues(issues, 'totals.shipping')}
                  {...register('totals.shipping', { valueAsNumber: true })}
                />
              </div>
            )}

            {/* Discount - show if has a value OR if evaluator flagged it OR has issues */}
            {(totals.discount != null && totals.discount !== 0
              || (getTotalsConfidence('discount') != null && getTotalsConfidence('discount') < 0.8)
              || getFieldIssues(issues, 'totals.discount').length > 0) && (
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm text-gray-600 whitespace-nowrap inline-flex items-center">
                  Discount:
                  {getTotalsConfidence('discount') != null && (
                    <span className={`ml-1 text-xs ${getTotalsConfidence('discount') < 0.8 ? 'text-yellow-600' : 'text-green-600'}`}>
                      ({Math.round(getTotalsConfidence('discount') * 100)}%)
                    </span>
                  )}
                  <IssuesIndicator issues={getFieldIssues(issues, 'totals.discount')} />
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="w-36 text-right"
                  confidence={getTotalsConfidence('discount')}
                  issues={getFieldIssues(issues, 'totals.discount')}
                  {...register('totals.discount', { valueAsNumber: true })}
                />
              </div>
            )}

            {/* Grand Total */}
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-300">
              <label className="text-sm font-medium text-gray-900 whitespace-nowrap inline-flex items-center">
                Grand Total:
                {getTotalsConfidence('grandTotal') != null && (
                  <span className={`ml-1 text-xs ${getTotalsConfidence('grandTotal') < 0.8 ? 'text-yellow-600' : 'text-green-600'}`}>
                    ({Math.round(getTotalsConfidence('grandTotal') * 100)}%)
                  </span>
                )}
                <IssuesIndicator issues={getFieldIssues(issues, 'totals.grandTotal')} />
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                className="w-36 text-right font-bold"
                confidence={getTotalsConfidence('grandTotal')}
                issues={getFieldIssues(issues, 'totals.grandTotal')}
                {...register('totals.grandTotal', { valueAsNumber: true })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default POItemsFields;
